import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { EnergyDataset, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const OFFICER_ROLES = ['ENERGY_OFFICER'];

type Tab = 'workbench' | 'public';

/** Monthly generation-mix dataset — draft -> submit -> verify (dual control, a different officer) -> publish, feeding the public dashboard (M35). */
export function EnergyDatasets() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENERGY_OFFICER') ?? false;
  const [tab, setTab] = useState<Tab>('workbench');

  return (
    <div>
      <PageHeader title="Energy Generation-Mix Datasets" subtitle="Monthly renewable/fossil generation mix — dual-control verification before it feeds the public dashboard (M35)." />
      <TabStrip tabs={[{ key: 'workbench', label: 'Analyst workbench' }, { key: 'public', label: 'Public dashboard preview' }]} active={tab} onChange={setTab} />
      {tab === 'workbench' ? <WorkbenchTab isOfficer={isOfficer} /> : <PublicPreviewTab />}
    </div>
  );
}

function WorkbenchTab({ isOfficer }: { isOfficer: boolean }) {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listQ = useApiQuery<Page<EnergyDataset>>('/api/v1/m35/energy-datasets?limit=50');

  return (
    <div>
      <div className="mb-4 flex justify-end">
        {isOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Record monthly dataset
          </button>
        ) : null}
      </div>
      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No datasets recorded" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Period</th>
                <th className="table-head">Total (MWh)</th>
                <th className="table-head">Renewable share</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(d.id)}>
                  <td className="table-cell font-mono text-xs font-medium">
                    {d.periodYear}-{String(d.periodMonth).padStart(2, '0')}
                  </td>
                  <td className="table-cell tabular-nums">{Number(d.totalGenerationMwh).toLocaleString()}</td>
                  <td className="table-cell tabular-nums">{d.renewableSharePercent}%</td>
                  <td className="table-cell">
                    <StatusBadge status={d.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <DetailModal id={selectedId} isOfficer={isOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<EnergyDataset>(`/api/v1/m35/energy-datasets/${id}`);
  const d = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m35/energy-datasets/${id}/${action}`);
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed — the backend rejects a verify attempt by the same officer who submitted it (dual control)');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={d ? `${d.periodYear}-${String(d.periodMonth).padStart(2, '0')}` : 'Energy dataset'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {d ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Status" value={<StatusBadge status={d.status} />} />
            <Detail label="Total generation" value={`${Number(d.totalGenerationMwh).toLocaleString()} MWh`} />
            <Detail label="Renewable" value={`${Number(d.renewableGenerationMwh).toLocaleString()} MWh (${d.renewableSharePercent}%)`} />
            <Detail label="Fossil" value={`${Number(d.fossilGenerationMwh).toLocaleString()} MWh`} />
            <Detail label="Data source" value={d.dataSource} />
          </div>
          <div className="flex h-6 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="bg-seagreen-500" style={{ width: `${d.renewableSharePercent}%` }} title="Renewable" />
            <div className="bg-slate-400" style={{ width: `${100 - Number(d.renewableSharePercent)}%` }} title="Fossil" />
          </div>
          {isOfficer && d.status === 'DRAFT' ? (
            <button className="btn-primary" disabled={busy} onClick={() => act('submit')}>
              Submit
            </button>
          ) : null}
          {isOfficer && d.status === 'SUBMITTED' ? (
            <>
              <p className="text-xs text-slate-500">Dual control: the verifying officer must be a different SeyID subject than {d.submittedBy}.</p>
              <button className="btn-primary" disabled={busy} onClick={() => act('verify')}>
                Verify
              </button>
            </>
          ) : null}
          {isOfficer && d.status === 'VERIFIED' ? (
            <button className="btn-success" disabled={busy} onClick={() => act('publish')}>
              Publish (announces via OpenGov)
            </button>
          ) : null}
          {d.publishedAt ? <p className="text-xs text-seagreen-600">Published {formatDateTime(d.publishedAt)}{d.openGovNoticeRef ? ` — OpenGov notice ${d.openGovNoticeRef}` : ''}</p> : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth());
  const [totalGenerationMwh, setTotalGenerationMwh] = useState('');
  const [renewableGenerationMwh, setRenewableGenerationMwh] = useState('');
  const [fossilGenerationMwh, setFossilGenerationMwh] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = Number.parseFloat(totalGenerationMwh) || 0;
  const renewable = Number.parseFloat(renewableGenerationMwh) || 0;
  const fossil = Number.parseFloat(fossilGenerationMwh) || 0;
  const sumMatches = Math.abs(renewable + fossil - total) <= 0.5;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m35/energy-datasets', {
        periodYear,
        periodMonth,
        totalGenerationMwh: total,
        renewableGenerationMwh: renewable,
        fossilGenerationMwh: fossil,
        dataSource,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record dataset');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record monthly generation-mix dataset" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Period year">
            <input className="field-input" type="number" value={periodYear} onChange={(e) => setPeriodYear(Number.parseInt(e.target.value, 10) || periodYear)} />
          </Field>
          <Field label="Period month (1-12)">
            <input className="field-input" type="number" min="1" max="12" value={periodMonth} onChange={(e) => setPeriodMonth(Number.parseInt(e.target.value, 10) || periodMonth)} />
          </Field>
        </div>
        <Field label="Total generation (MWh)">
          <input className="field-input" type="number" value={totalGenerationMwh} onChange={(e) => setTotalGenerationMwh(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Renewable generation (MWh)">
            <input className="field-input" type="number" value={renewableGenerationMwh} onChange={(e) => setRenewableGenerationMwh(e.target.value)} />
          </Field>
          <Field label="Fossil generation (MWh)">
            <input className="field-input" type="number" value={fossilGenerationMwh} onChange={(e) => setFossilGenerationMwh(e.target.value)} />
          </Field>
        </div>
        {!sumMatches ? <p className="text-xs text-rose-600">Renewable + fossil must sum to total (within 0.5 MWh).</p> : null}
        <Field label="Data source">
          <input className="field-input" value={dataSource} onChange={(e) => setDataSource(e.target.value)} placeholder="e.g. PUC monthly generation returns" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !sumMatches || !totalGenerationMwh || !dataSource.trim()} onClick={submit}>
            {busy ? 'Saving…' : 'Record dataset'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PublicPreviewTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const q = useApiQuery<EnergyDataset>(`/api/v1/m35/energy-datasets/${year}/${month}`);

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center gap-2">
        <input className="field-input !w-auto" type="number" value={year} onChange={(e) => setYear(Number.parseInt(e.target.value, 10) || year)} />
        <input className="field-input !w-auto" type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Number.parseInt(e.target.value, 10) || month)} />
      </div>
      {q.error ? (
        <EmptyState title="No published dataset for this period" hint="Only PUBLISHED datasets are visible on the public feed; staff see drafts from the Analyst workbench tab." />
      ) : q.loading ? (
        <Spinner />
      ) : q.data ? (
        <div className="space-y-3">
          <div className="text-lg font-bold text-slate-900">
            {q.data.renewableSharePercent}% renewable — {year}-{String(month).padStart(2, '0')}
          </div>
          <div className="flex h-8 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="bg-seagreen-500" style={{ width: `${q.data.renewableSharePercent}%` }} />
            <div className="bg-slate-400" style={{ width: `${100 - Number(q.data.renewableSharePercent)}%` }} />
          </div>
          <p className="text-xs text-slate-500">Source: {q.data.dataSource}</p>
        </div>
      ) : null}
    </div>
  );
}
